import * as React from 'react';

import type { W_WorkOrderReport_UnassignedEntries_VM } from 'ab-api/web/workOrderReport/timeCardUnassignEntriesSidePanel';

import { TimeSheetEntryWorkType } from '@acceligentllc/shared/enums/timeSheetEntryWorkType';

import DropdownComponent from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';

interface AvailableWorkOrder {
	id: number;
	workOrderCode: string;
	supervisorName: string;
	dueDate: string;
	customerCompanyName: Nullable<string>;
}

interface EntryIdWorkOrderIdCombination {
	[entryId: number]: number;
}

type Props = {
	unassignedEntry: W_WorkOrderReport_UnassignedEntries_VM;
	setEntryWorkOrderCombination: (entryIdWoIdObj: EntryIdWorkOrderIdCombination) => void;
	removeEntry: (entryId: number) => void;
};

const WORK_TYPE_LABELS = {
	[TimeSheetEntryWorkType.BREAK]: 'Break',
	[TimeSheetEntryWorkType.SHOP]: 'Shop',
	[TimeSheetEntryWorkType.TRAVEL]: 'Travel',
	[TimeSheetEntryWorkType.JOB]: 'Job',
};

const workTypeIcons: Record<TimeSheetEntryWorkType, string> = {
	[TimeSheetEntryWorkType.BREAK]: 'icon-break',
	[TimeSheetEntryWorkType.SHOP]: 'icon-home',
	[TimeSheetEntryWorkType.TRAVEL]: 'icon-travel',
	[TimeSheetEntryWorkType.JOB]: 'icon-equipment',
};

const renderItemOption = (wo: AvailableWorkOrder) => {
	if (!wo.id) {
		return (
			<div className={styles['item-option__details']}>
				No options
			</div>
		);
	}

	return (
		<>
			<div className={styles['item-option']}>
				<span>{wo.workOrderCode}</span>
				<span className={styles['item-option__details']}>
					{wo.supervisorName}
				</span>
				<span className={styles['item-option__details']}>
					{wo.dueDate} | {wo.customerCompanyName}
				</span>
			</div>
		</>
	);
};

const UnassignedEntry: React.FC<Props> = (props) => {
	const { unassignedEntry, setEntryWorkOrderCombination, removeEntry } = props;
	const [selectedworkOrder, setSelectedworkOrder] = React.useState<Nullable<AvailableWorkOrder>>(null);

	const saveSelection = React.useCallback((workOrder: AvailableWorkOrder) => {
		if (!workOrder) {
			return;
		}
		setEntryWorkOrderCombination({ [unassignedEntry.id]: workOrder.id });
		setSelectedworkOrder(workOrder);
	}, [setEntryWorkOrderCombination, unassignedEntry.id]);

	const renderSelectedItem = React.useCallback(() => {
		return <>{selectedworkOrder ? `${selectedworkOrder.workOrderCode}` : ''}</>;
	}, [selectedworkOrder]);

	const removeEntryFromList = React.useCallback(() => {
		removeEntry(unassignedEntry.id);
	}, [removeEntry, unassignedEntry.id]);

	return (
		<div className={styles.timeSheetEntry} key={unassignedEntry.id}>
			<div className={styles.workTypeIcon}>
				{workTypeIcons[unassignedEntry.workType] && <span className={workTypeIcons[unassignedEntry.workType]} />}
			</div>
			<div className={styles.workTypeInfo}>
				<div className={styles.firstRow}>
					<div className={styles.workType}>
						{WORK_TYPE_LABELS[unassignedEntry.workType]}
					</div>
					<div className={styles.workOrderCodeSeparator} />
					<div className={styles.unassigned}>
						Unassigned
					</div>
				</div>
				<div className={styles.secondRow}>
					<div className={styles.startEndTime}>
						{unassignedEntry.startTime}
					</div>
					<div className={styles.startEndTime}>
						-
					</div>
					<div className={styles.startEndTime}>
						{unassignedEntry.endTime}
					</div>
				</div>
			</div>

			<div className={styles.dropdownPart}>
				<DropdownComponent<Partial<AvailableWorkOrder>>
					containerClassName={styles['item-dropdown']}
					fixed={true}
					id="workOrder-dropdown"
					onValueChange={saveSelection}
					options={unassignedEntry.availableWorkOrders.length ? unassignedEntry.availableWorkOrders : [{ id: undefined }]}
					placeholder="Select Work Order"
					renderMenuItem={renderItemOption}
					renderSelected={renderSelectedItem}
					valueKey="id"
					withBorder={true}
					withCaret={true}
				/>
			</div>

			<div className={styles.deleteIcon}>
				<span className="icon-delete" onClick={removeEntryFromList} />
			</div>
		</div>
	);
};

export default React.memo(UnassignedEntry);
